import React, { useContext, useEffect, useRef, useState } from "react"
import SlotCounter from "react-slot-counter"
import { AIStateContext } from "../providers/AIStateProvider.jsx"
import { GlowingButtonContainer } from "../GlowingButtonContainer.jsx"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

export const CreditsBalanceDisplay = () => {
	const { aiState, setAIState } = useContext(AIStateContext)
	const { t } = useTranslation()
	const navigate = useNavigate()

	const creditsIcon = (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			width="1rem"
			height="1rem"
			viewBox="0 0 24 24"
			style={{ color: "#fff" }}
		>
			<g fill="none" fillRule="evenodd">
				<path d="M24 0v24H0V0zM12.594 23.258l-.012.002l-.071.035l-.02.004l-.014-.004l-.071-.036c-.01-.003-.019 0-.024.006l-.004.01l-.017.428l.005.02l.01.013l.104.074l.015.004l.012-.004l.104-.074l.012-.016l.004-.017l-.017-.427c-.002-.01-.009-.017-.016-.018m.264-.113l-.014.002l-.184.093l-.01.01l-.003.011l.018.43l.005.012l.008.008l.201.092c.012.004.023 0 .029-.008l.004-.014l-.034-.614c-.003-.012-.01-.02-.02-.022m-.715.002a.023.023 0 0 0-.027.006l-.006.014l-.034.614c0 .012.007.02.017.024l.015-.002l.201-.093l.01-.008l.003-.011l.018-.43l-.003-.012l-.01-.01z"></path>
				<path
					fill="currentColor"
					d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10s10-4.477 10-10S17.523 2 12 2m0 1a9 9 0 0 1-9 9a9 9 0 0 1 9 9a9 9 0 0 1 9-9a9 9 0 0 1-9-9"
				></path>
			</g>
		</svg>
	)
	const loadingIcon = (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			style={{ display: "block" }}
			width="0.8rem"
			height="0.8rem"
			viewBox="0 0 24 24"
		>
			<circle cx="4" cy="12" r="3" fill="currentColor">
				<animate
					id="svgSpinners3DotsBounce0"
					attributeName="cy"
					begin="0;svgSpinners3DotsBounce1.end+0.25s"
					calcMode="spline"
					dur="0.6s"
					keySplines=".33,.66,.66,1;.33,0,.66,.33"
					values="12;6;12"
				></animate>
			</circle>
			<circle cx="12" cy="12" r="3" fill="currentColor">
				<animate
					attributeName="cy"
					begin="svgSpinners3DotsBounce0.begin+0.1s"
					calcMode="spline"
					dur="0.6s"
					keySplines=".33,.66,.66,1;.33,0,.66,.33"
					values="12;6;12"
				></animate>
			</circle>
			<circle cx="20" cy="12" r="3" fill="currentColor">
				<animate
					id="svgSpinners3DotsBounce1"
					attributeName="cy"
					begin="svgSpinners3DotsBounce0.begin+0.2s"
					calcMode="spline"
					dur="0.6s"
					keySplines=".33,.66,.66,1;.33,0,.66,.33"
					values="12;6;12"
				></animate>
			</circle>
		</svg>
	)

	const [showSlots, setShowSlots] = useState(false)
	const [rolling, setRolling] = useState(false)
	const [numberOverride, setNumberOverride] = useState(null)
	const [newCredits, setNewCredits] = useState(null)
	const intervalRef = useRef(null)

	const rollSlots = async () => {
		if (rolling) return

		setRolling(true)
		setShowSlots(false)

		await new Promise((resolve) => setTimeout(resolve, 5000))
		const newCredits = Math.floor(Math.random() * (200 - 50 + 1) + 50)

		setNewCredits(newCredits)
		setTimeout(() => setNewCredits(null), 2000)

		setAIState({ creditsAvailable: aiState.creditsAvailable + newCredits })

		setRolling(false)
	}

	useEffect(() => {
		if (rolling) {
			intervalRef.current = setInterval(() => {
				setNumberOverride(
					Math.floor(Math.random() * (999 - 100 + 1) + 100),
				)
			}, 500)
		} else {
			setNumberOverride(null)
			clearInterval(intervalRef.current)
		}

		return () => clearInterval(intervalRef.current)
	}, [rolling])

	const borderRadius = showSlots ? "0.5rem 0rem 0rem 0.5rem" : "0.5rem"
	const creditsDisplayed = aiState.creditsAvailable
	const creditsBelowZero = aiState.creditsAvailable < 0

	return (
		<div className="credits-balance-display-wrapper">
			<GlowingButtonContainer
				intensity="small"
				disabled={showSlots === false}
			>
				<div style={{ display: "flex", position: "relative" }}>
					<div
						onClick={() => navigate("/ai")}
						style={{
							background: creditsBelowZero ? "#e71f1f" : "#111",
							padding: "0.3rem 0.5rem",
							borderRadius: borderRadius,
							display: "flex",
							alignItems: "center",
							gap: "0.5rem",
							position: "relative",
							cursor: "pointer",
							transition: "border-radius 1s",
						}}
					>
						<div
							style={{
								display: "flex",
								alignItems: "center",
								gap: "0.3rem",
							}}
						>
							{creditsIcon}
							<span style={{ fontSize: "0.8rem", color: "#fff" }}>
								{t("ai.credits_available")}:
							</span>
						</div>
						<span
							translate="no"
							style={{
								fontSize: "0.85rem",
								fontWeight: "600",
								fontFamily: "monospace",
							}}
						>
							<SlotCounter
								key={numberOverride || creditsDisplayed}
								duration={rolling ? 0.5 : 1}
								value={numberOverride || creditsDisplayed}
								charClassName="slot-counter"
							/>
						</span>

						<div
							className="shadow border"
							style={{
								opacity: aiState.loadingCreditsAvailable
									? "1"
									: "0",
								transition: "opacity 0.35s ease-in-out",
								position: "absolute",
								top: "-0.5rem",
								right: "-0.5rem",
								background: "#fff",
								padding: "0rem 0.3rem",
								borderRadius: "0.5rem",
								color: "#222",
							}}
						>
							{loadingIcon}
						</div>
					</div>

					<div
						onClick={rollSlots}
						style={{
							width: showSlots ? "2rem" : "0rem",
							opacity: showSlots ? "1" : "0",
							transition: "width 1s, opacity 1s",
							background: "#e2e2e2",
							color: "#555",
							borderRadius: "0rem 0.5rem 0.5rem 0rem",
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
							cursor: "pointer",
						}}
					>
						<svg
							xmlns="http://www.w3.org/2000/svg"
							style={{ display: "block" }}
							width="1.5rem"
							height="1.5rem"
							viewBox="0 0 24 24"
						>
							<path
								fill="currentColor"
								d="m5 12l2-4V7H4v1h2l-2 4m5 0l2-4V7H8v1h2l-2 4m5 0l2-4V7h-3v1h2l-2 4m9-10c-1.1 0-2 .9-2 2c0 .7.4 1.4 1 1.7V17h-3v-2c.6 0 1-.4 1-1V5c0-.6-.4-1-1-1h-3.8c-.8-1.2-2.2-2-3.7-2s-2.9.8-3.7 2H2c-.6 0-1 .4-1 1v9c0 .6.4 1 1 1v7h15v-3h3c1.1 0 2-.9 2-2V5.7c.6-.3 1-1 1-1.7c0-1.1-.9-2-2-2m-8 17H6v-2h7zm3-6H3V6h13z"
							></path>
						</svg>
					</div>

					<div
						className="shadow border"
						style={{
							position: "absolute",
							left: "50%",
							bottom: newCredits ? "-2.5rem" : "0rem",
							opacity: newCredits ? "1" : "0",
							transition: "bottom 0.5s, opacity 0.2s",
							zIndex: "-1",
							background: "#fff",
							borderRadius: "0.5rem",
							padding: "0.1rem 0.4rem",
						}}
					>
						<span
							style={{
								fontSize: "0.8rem",
								fontWeight: "bold",
								color: "#008f18",
							}}
						>
							+{newCredits}
						</span>
					</div>

					<style>{`
						.bg-color-rotation {
							animation: bg-color-rotation-animation 3s infinite;
						}
						
						@keyframes bg-color-rotation-animation {
							0% {
								filter: hue-rotate(0deg);
							}
							
							50% {
								filter: hue-rotate(180deg);
							}
							
							100% {
								filter: hue-rotate(360deg);
							}
						}
	
						.slot-counter {
							background: #fff;
							color: #000;
							border-radius: 0.2rem;
							border: 1px solid #bbb;
							padding: 0rem 0.1rem;
							margin: 0rem 0.1rem;
						}

						.credits-balance-display-wrapper {
							display: none;
						}

						@media (min-width: 550px) {
							.credits-balance-display-wrapper {
								display: block;
							}
						}
					`}</style>
				</div>
			</GlowingButtonContainer>
		</div>
	)
}
